import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Banknote, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DEPOSIT_METHODS = [
  { value: "revolut_link", label: "Revolut link sent manually" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "card_in_person", label: "Card in person" },
];

/**
 * Replace the standard €50 deposit with what the customer ACTUALLY paid.
 * ADMIN ONLY. This REPLACES the figure — it never adds to it — so the
 * preview below is always `total − discount − new deposit`, never
 * `old balance − new deposit`.
 */
export const CorrectDepositDialog = ({ open, onClose, booking, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("revolut_link");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const total = Number(booking?.total_price || 0);
  const currentDeposit = Number(booking?.deposit_amount ?? 50);
  const discount = Math.max(
    0,
    Number(booking?.promo_discount_applied?.amount
      ?? booking?.promo_discount_applied
      ?? 0) || 0
  );
  const charged = Math.max(0, total - discount);

  const parsed = useMemo(() => {
    const n = Number(String(amount).replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  const projectedBalance = Math.max(0, charged - parsed);
  const tooBig = parsed > charged + 0.009;
  const valid = parsed > 0 && !tooBig;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/bookings/${booking.booking_id}/correct-deposit`,
        { amount: parsed, method, note }
      );
      toast.success(
        `Deposit corrected ${money(data.previous_deposit_amount)} → ` +
        `${money(data.deposit_amount)} · ${money(data.balance_due)} due on collection`
      );
      if (data.collection_email_stale) {
        toast.warning(
          "The \"Your Car is Ready\" email already went out with the old balance — " +
          "resend it from Change services if the customer needs the new figure.",
          { duration: 9000 }
        );
      }
      setAmount("");
      setNote("");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not correct the deposit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg"
                     data-testid="correct-deposit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-400" />
            Correct deposit received
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Set the deposit to the amount that actually landed. This REPLACES
            the current figure — anything paid after this goes through the
            balance buttons.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-400">Job total</span>
              <span className="font-semibold" data-testid="correct-deposit-total">
                {money(total)}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">Promo discount</span>
                <span className="text-amber-300">−{money(discount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-400">Deposit on file now</span>
              <span data-testid="correct-deposit-current">{money(currentDeposit)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="correct-deposit-amount">Deposit actually received</Label>
            <Input
              id="correct-deposit-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="600.00"
              className="bg-slate-950 border-slate-700"
              data-testid="correct-deposit-amount"
            />
            {tooBig && (
              <p className="text-xs text-red-300" data-testid="correct-deposit-too-big">
                {money(parsed)} is more than the {money(charged)} this job comes to —
                that is an overpayment, not a deposit.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>How was it paid?</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="bg-slate-950 border-slate-700"
                             data-testid="correct-deposit-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-white">
                {DEPOSIT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="correct-deposit-note">Note (optional)</Label>
            <Textarea
              id="correct-deposit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Revolut link sent by text on 8 Oct, paid same day"
              className="bg-slate-950 border-slate-700 min-h-[70px]"
              data-testid="correct-deposit-note"
            />
          </div>

          {valid && (
            <div className="rounded-lg border border-emerald-600/40 bg-emerald-950/20 p-3 text-sm"
                 data-testid="correct-deposit-preview">
              <div className="flex justify-between">
                <span className="text-slate-300">Deposit</span>
                <span className="text-slate-400">
                  {money(currentDeposit)} →{" "}
                  <strong className="text-emerald-300">{money(parsed)}</strong>
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-300">Due on collection</span>
                <strong className="text-emerald-300"
                        data-testid="correct-deposit-projected-balance">
                  {money(projectedBalance)}
                </strong>
              </div>
            </div>
          )}

          {booking?.collection_email_sent && (
            <p className="flex items-start gap-2 text-xs text-amber-300"
               data-testid="correct-deposit-email-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              The "Your Car is Ready" email already went out quoting the old
              balance. The customer is not emailed about this correction.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}
                  data-testid="correct-deposit-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="correct-deposit-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save corrected deposit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** The before/after trail, shown under Payments on the job card. */
export const DepositCorrectionHistory = ({ corrections }) => {
  if (!corrections?.length) return null;
  return (
    <div className="space-y-1 pt-1" data-testid="deposit-correction-history">
      {corrections.map((c) => (
        <p key={c.correction_id || c.corrected_at}
           className="text-xs text-slate-400"
           data-testid={`deposit-correction-${c.correction_id}`}>
          <span className="text-slate-300 font-medium">
            {money(c.old_amount)} → {money(c.new_amount)}
          </span>
          {" · "}{c.method_label}
          {" · "}{new Date(c.corrected_at).toLocaleDateString("en-IE", {
            day: "numeric", month: "short" })}
          {c.corrected_by_name ? ` · ${c.corrected_by_name}` : ""}
          {c.note ? <span className="block text-slate-500">{c.note}</span> : null}
        </p>
      ))}
    </div>
  );
};

export default CorrectDepositDialog;
